import React from 'react';
import { Navigate } from 'react-router-dom';
import {
  selectAccessDealers,
  selectAccessManufacturers,
  selectAccessOrders,
  selectAccessTechnicians,
  selectAccessUsers,
  selectCurrentUser,
  selectManageManufacturers,
} from '../features/user';
import { useAppSelector } from '../store/hooks';
import { UserRole } from '../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiresUserAccess?: boolean;
  requiresManufacturerAccess?: boolean;
  requiresManufacturerManage?: boolean;
  requiresDealerAccess?: boolean;
  requiresTechnicianAccess?: boolean;
  requiresOrderAccess?: boolean;
  requiresReportAccess?: boolean;
}

const ProtectedRoute = ({
  children,
  requiredRole,
  requiresUserAccess,
  requiresManufacturerAccess,
  requiresManufacturerManage,
  requiresDealerAccess,
  requiresTechnicianAccess,
  requiresOrderAccess,
  requiresReportAccess,
}: ProtectedRouteProps) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const canAccessUsers = useAppSelector(selectAccessUsers);
  const canAccessManufacturers = useAppSelector(selectAccessManufacturers);
  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const canAccessDealers = useAppSelector(selectAccessDealers);
  const canAccessTechnicians = useAppSelector(selectAccessTechnicians);
  const canAccessOrders = useAppSelector(selectAccessOrders);

  // Report access: Administrator, Supervisor, and CSR roles only
  const canAccessReports =
    currentUser?.role === UserRole.ADMINISTRATOR ||
    currentUser?.role === UserRole.SUPERVISOR ||
    currentUser?.role === UserRole.CSR;

  // Check if user is authenticated
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  // Check role-based access - this returns early if role matches
  if (requiredRole && currentUser?.role === requiredRole) {
    return <>{children}</>;
  }

  // Check permission-based access requirements
  const accessDenied = checkPermissionRequirements(
    {
      requiresUserAccess,
      requiresManufacturerAccess,
      requiresManufacturerManage,
      requiresDealerAccess,
      requiresTechnicianAccess,
      requiresOrderAccess,
      requiresReportAccess,
    },
    {
      canAccessUsers,
      canAccessManufacturers,
      canManageManufacturers,
      canAccessDealers,
      canAccessTechnicians,
      canAccessOrders,
      canAccessReports,
    }
  );

  if (accessDenied) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Helper function to check all permission requirements
const checkPermissionRequirements = (
  requirements: {
    requiresUserAccess?: boolean;
    requiresManufacturerAccess?: boolean;
    requiresManufacturerManage?: boolean;
    requiresDealerAccess?: boolean;
    requiresTechnicianAccess?: boolean;
    requiresOrderAccess?: boolean;
    requiresReportAccess?: boolean;
  },
  permissions: {
    canAccessUsers: boolean;
    canAccessManufacturers: boolean;
    canManageManufacturers: boolean;
    canAccessDealers: boolean;
    canAccessTechnicians: boolean;
    canAccessOrders: boolean;
    canAccessReports: boolean;
  }
): boolean => {
  const {
    requiresUserAccess,
    requiresManufacturerAccess,
    requiresManufacturerManage,
    requiresDealerAccess,
    requiresTechnicianAccess,
    requiresOrderAccess,
    requiresReportAccess,
  } = requirements;

  const {
    canAccessUsers,
    canAccessManufacturers,
    canManageManufacturers,
    canAccessDealers,
    canAccessTechnicians,
    canAccessOrders,
    canAccessReports,
  } = permissions;

  return (
    isAccessDenied(requiresUserAccess, canAccessUsers) ||
    isAccessDenied(requiresManufacturerAccess, canAccessManufacturers) ||
    isAccessDenied(requiresManufacturerManage, canManageManufacturers) ||
    isAccessDenied(requiresDealerAccess, canAccessDealers) ||
    isAccessDenied(requiresTechnicianAccess, canAccessTechnicians) ||
    isAccessDenied(requiresOrderAccess, canAccessOrders) ||
    isAccessDenied(requiresReportAccess, canAccessReports)
  );
};

// Helper function to check individual permission requirement
const isAccessDenied = (isRequired?: boolean, hasPermission?: boolean): boolean => {
  return Boolean(isRequired && !hasPermission);
};

export default ProtectedRoute;
